import React, { useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import AddLink from "controls/global/add-link/AddLink";
import StewartSortLabel from "controls/global/stewart-table/StewartSortLabel";
import { getComparator, Order, stableSort } from "utils/sorting";
import {
  PolicyManagementRecord,
  PolicyNote,
} from "entities/UIModel/PolicyManagementRecord";
import { AddNoteModal, EditNoteModal, ViewNoteModal } from "../modals";
import ViewNoteTooltipCell from "./ViewNoteTooltipCell";
import usePolicyNotes from "./usePolicyNotes";

const SectionCard = styled(Box)({
  backgroundColor: colors.white,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  padding: padding.medium1,
});

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  marginBottom: gaps.small1,
  paddingBottom: gaps.xsmall2,
  borderBottom: `1px solid ${colors.grey09}`,
});

const SectionTitle = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const Count = styled(Typography)({
  fontSize: fontSize.xsmall,
  color: colors.grey11,
});

const HeaderCell = styled(TableCell)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  whiteSpace: "nowrap",
  padding: `${padding.xsmall2} ${padding.small1}`,
});

const BodyCell = styled(TableCell)({
  fontSize: fontSize.small,
  color: colors.black,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  padding: `${padding.xsmall2} ${padding.small1}`,
});

// Narrow, fixed-width cell for the trailing edit action so it doesn't stretch
// the row or push the data columns around.
const ActionCell = styled(BodyCell)({
  padding: `0 ${padding.xsmall2}`,
  width: "48px",
});

function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return "---";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

// Header columns mirror Policy Inventory's notes table (DESCRIPTION / USER /
// DATE/TIME). The trailing actions column holds the per-row edit affordance.
// Only these text columns are sortable (kept off `keyof PolicyNote` so the
// optional `id` field doesn't widen the comparator's key constraint).
type NoteSortKey = "description" | "type" | "by" | "at";

interface HeadCell {
  id: NoteSortKey;
  label: string;
  width: string;
}

const headCells: HeadCell[] = [
  { id: "description", label: "Description", width: "auto" },
  { id: "type", label: "Note Type", width: "160px" },
  { id: "by", label: "User", width: "180px" },
  { id: "at", label: "Date / Time", width: "200px" },
];

interface NotesSectionProps {
  record: PolicyManagementRecord;
}

const NotesSection = ({ record }: NotesSectionProps) => {
  // Notes are sourced from the backend (GetPolicyNoteListByTransaction), added
  // via InsertPolicyNote, and edited via UpdatePolicyNote.
  const { notes, addNote, editNote } = usePolicyNotes(
    record.PolicyTransactionID,
    record.PolicyNumber
  );
  const [addOpen, setAddOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(true);

  const [viewNote, setViewNote] = useState<string | undefined>(undefined);
  const [viewNoteOpen, setViewNoteOpen] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState<string | undefined>(undefined);
  const [editNoteOpen, setEditNoteOpen] = useState(false);

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<NoteSortKey>("at");

  const allNotes: PolicyNote[] = notes.map((n) => {
    const text = n.text || n.description;
    return { ...n, text, description: text };
  });

  const handleRequestSort = (property: NoteSortKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleViewNote = (text: string | undefined) => {
    setViewNote(text);
    setViewNoteOpen(true);
  };

  const handleEditOpen = (id: number | undefined, text: string | undefined) => {
    if (id == null) return;
    setEditId(id);
    setEditText(text);
    setEditNoteOpen(true);
  };

  const handleEditSubmit = (text: string) => {
    if (editId === null) return;
    void editNote(editId, text);
  };

  const handleAdd = (text?: string) => {
    if (!text) return;
    void addNote(text);
  };

  const sortedNotes = stableSort(
    allNotes.map((n, index) => ({ ...n, index })),
    getComparator(order, orderBy)
  );

  return (
    <SectionCard>
      <SectionHeader
        sx={{
          cursor: "pointer",
          borderBottom: sectionOpen ? `1px solid ${colors.grey09}` : "none",
        }}
        onClick={() => setSectionOpen((o) => !o)}
      >
        <SectionTitle>Notes</SectionTitle>
        <Count>({allNotes.length})</Count>
        <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          {sectionOpen ? (
            <ExpandLessIcon sx={{ color: colors.blue15 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: colors.blue15 }} />
          )}
        </Box>
      </SectionHeader>

      <Collapse in={sectionOpen} unmountOnExit>
        {allNotes.length === 0 ? (
          <Typography
            sx={{
              fontSize: fontSize.small,
              color: colors.grey11,
              padding: `${padding.small1} 0`,
            }}
          >
            There are currently no notes on this record. Click the Add Note
            button below to add notes.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{ tableLayout: "fixed", width: "100%" }}
            >
              <TableHead>
                <TableRow>
                  {headCells.map((cell) => (
                    <HeaderCell key={cell.id} style={{ width: cell.width }}>
                      <StewartSortLabel
                        active={orderBy === cell.id}
                        direction={orderBy === cell.id ? order : "asc"}
                        onRequestSort={() => handleRequestSort(cell.id)}
                      >
                        {cell.label}
                      </StewartSortLabel>
                    </HeaderCell>
                  ))}
                  <HeaderCell style={{ width: "48px" }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedNotes.map((n) => {
                  const noteText = n.text || n.description;
                  return (
                    <TableRow key={`${n.at}-${n.index}`}>
                      <BodyCell>
                        <ViewNoteTooltipCell
                          title={noteText || "---"}
                          handleViewNoteOpen={() => handleViewNote(noteText)}
                        />
                      </BodyCell>
                      <BodyCell>{n.type || "---"}</BodyCell>
                      <BodyCell>{n.by || "---"}</BodyCell>
                      <BodyCell>{formatDateTime(n.at)}</BodyCell>
                      <ActionCell align="right">
                        <Tooltip title="Edit note" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleEditOpen(n.id, noteText)}
                            sx={{ color: colors.blue01 }}
                          >
                            <EditNoteOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ActionCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box sx={{ marginTop: gaps.small1 }}>
          <AddLink disabled={false} onClick={() => setAddOpen(true)}>
            Add Note
          </AddLink>
        </Box>
      </Collapse>

      <AddNoteModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        record={record}
        onSubmit={handleAdd}
      />

      <EditNoteModal
        open={editNoteOpen}
        onClose={() => setEditNoteOpen(false)}
        note={editText}
        onSubmit={handleEditSubmit}
      />

      <ViewNoteModal
        open={viewNoteOpen}
        onClose={() => setViewNoteOpen(false)}
        note={viewNote}
      />
    </SectionCard>
  );
};

export default NotesSection;
